import {AbstractControl, FormGroup} from "@angular/forms";
import {PitActivityFinancingAmountBySource} from "../../pit-activities/domain/pit-activity-financing-amount-by-source";
import {PitResponsibleUser} from "../../pit-shared/domain/pit-responsible-user";
import {isObservable, Observable, of} from "rxjs";
import {fromPromise, isPromise} from "rxjs/internal-compatibility";
const keysIn = require('lodash.keysin');

export type ValueGetter = (obj: any, prop: string|number) => any;

export class DxCoreUtils {
  /**
   * Always returns the empty string ''
   * @returns {string}
   */
  public static emptyStringGetter(): string {
    return '';
  }

  /**
   * Returns the appropriate getter function for this kind of prop.
   * If prop == null, returns the emptyStringGetter.
   */
  public static getterForProp(prop: string): ValueGetter {
    if (prop == null) return DxCoreUtils.emptyStringGetter;

    if (typeof prop === 'number') {
      return DxCoreUtils.numericIndexGetter;
    } else {
      // deep or simple
      if (prop.indexOf('.') !== -1) {
        return DxCoreUtils.deepValueGetter;
      } else {
        return DxCoreUtils.shallowValueGetter;
      }
    }
  }

  /**
   * Returns the value at this numeric index.
   * @param row array of values
   * @param index numeric index
   * @returns {any} or '' if invalid index
   */
  public static numericIndexGetter(row: any[], index: number): any {
    if (row == null) return '';
    // mimic behavior of deepValueGetter
    if (!row || index == null) return row;

    const value = row[index];
    if (value == null) return '';
    return value;
  }

  /**
   * Returns the value of a field.
   * (more efficient than deepValueGetter)
   * @param obj object containing the field
   * @param fieldName field name string
   * @returns {any}
   */
  public static shallowValueGetter(obj: any, fieldName: string): any {
    if (obj == null) return '';
    if(!obj || !fieldName) return obj;

    const value = obj[fieldName];
    if (value == null) return '';
    return value;
  }

  /**
   * Returns a deep object given a string. zoo['animal.type']
   * @param {object} obj
   * @param {string} path
   */
  public static deepValueGetter(obj: any, path: string): any {
    if (obj == null) return '';
    if(!obj || !path) return obj;

    // check if path matches a root-level field
    // { "a.b.c": 123 }
    let current = obj[path];
    if (current !== undefined) return current;

    current = obj;
    const split = path.split('.');

    if(split.length) {
      for(let i = 0; i < split.length; i++) {
        current = current[split[i]];

        // if found undefined, return empty string
        if(current === undefined || current === null) return '';
      }
    }

    return current;
  }


  public static orderByComparator(a: any, b: any): number {
    if (a === null || typeof a === 'undefined') a = 0;
    if (b === null || typeof b === 'undefined') b = 0;
    if (a instanceof Date && b instanceof Date) {
      if (a < b) return -1;
      if (a > b) return 1;
    } else if ((isNaN(parseFloat(a)) || !isFinite(a)) || (isNaN(parseFloat(b)) || !isFinite(b))) {
      // Convert to string in case of a=0 or b=0
      a = String(a);
      b = String(b);
      // Isn't a number so lowercase the string to properly compare
      if (a.toLowerCase() < b.toLowerCase()) return -1;
      if (a.toLowerCase() > b.toLowerCase()) return 1;
    } else {
      // Parse strings as numbers to compare properly
      if (parseFloat(a) < parseFloat(b)) return -1;
      if (parseFloat(a) > parseFloat(b)) return 1;
    }

    // equal each other
    return 0;
  }


  /**
   * @return only deleted elements
   */
  public static replaceOrAppend(arr: any[], el: any, predicate: (a: any) => boolean): any[] {
    const index = arr.findIndex(predicate);
    return arr.splice(index, index >= 0 ? 1 : 0, el);
  }

  public static equalArrays(arr1: any[], arr2: any[], isEquals: (a1: any, a2: any) => boolean): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }
    for (let index = 0; index < arr1.length; index++) {
      if (!isEquals(arr1[index], arr2[index])) {
        return false;
      }
    }
    return true;
  }

  public static randomHexaDecimal(): string {
    return Math.ceil(Math.random()*(new Date().getTime())).toString(16);
  }

  public static getControlId(control: AbstractControl): string {
    if (!control) {
      return null;
    }
    const fg = control.parent as FormGroup;
    if (!fg) {
      return null;
    }
    const entry = Object.entries(fg.controls).find(entry => {
      const val = entry[1];
      return val === control;
    });
    const key = entry[0];
    return key;
  }

  public static getScrollPosition(): number {
    return document.querySelector('.dx-navigator__body').scrollTop;
  }

  public static setScrollPosition(value: number) {
    if (value)
      document.querySelector('.dx-navigator__body').scrollTop = value;
  }

  /**
   *  helps to distinct items of ngForOf
   */
  public static trackById(index: number, item: {id: any}): any {
    return item.id;
  }

  public static toObservable<T>(anCallback: T | Observable<T> | PromiseLike<T>): Observable<T> {
    if (isObservable(anCallback)) {
      return anCallback;
    }
    if (isPromise(anCallback)) {
      return fromPromise(anCallback);
    }
    return of(anCallback);
  }
}
